import React, { ChangeEventHandler, ReactElement, useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import { RADOM_COLORS } from '../util/Constants'
import Tooltip from './Tooltip'
import { DropdownContentBody } from './Dropdown'

interface ITextInputWithSuggetsionsProps {
  label: string
  type?: any
  value: string | number
  pattern?: string
  placeholder?: string
  onChange: ChangeEventHandler<HTMLElement>
  onKeyDown?: React.KeyboardEventHandler
  setSelectedItems: (items: string[]) => void
  required?: boolean
  disabled?: boolean
  suggestionList: string[]
  inputStyle?: React.CSSProperties
}

export const IconButtonInner = styled.div`
  display: flex;
  gap: 8px;
  font-size: 12px;
  align-items: center;
  justify-content: center;
`

export const BaseInputStyles = `
  border: 1px solid transparent;
  background-color: ${RADOM_COLORS.INPUT_DEFAULT};
  border-radius: 10px;
  padding: 8px 14px;
  width: 100%;
  outline: none;
  box-shadow: 0 1px 1px rgb(226 228 239 / 50%);
  transition: 0.2s ease all;

  &:hover {
    background-color: ${RADOM_COLORS.GRAY9};
  }

  :focus {
    border: 1px solid ${RADOM_COLORS.BLUE5};
    background-color: transparent;
    // box-shadow: 0 0 5px ${RADOM_COLORS.GRAY_DARK};
  }

  ::placeholder {
    color: ${RADOM_COLORS.GRAY_DARKER};
  }
`
const DropdownListItem = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px;
  cursor: pointer;

  :hover {
    background-color: ${RADOM_COLORS.GRAY_LIGHTEST};
  }
`

export const TextArea = styled.textarea<{ disabled?: boolean }>`
  ${BaseInputStyles}
  border-radius: 10px;
  max-width: 100%;
  width: 100%;
  box-sizing: border-box;
  resize: vertical;
  min-height: 50px;
`

export const OptionalLabel = styled.div`
  max-width: min-content;
  background-color: ${RADOM_COLORS.GRAY_MED};
  border-radius: 10px;
  padding: 3px 8px;
  color: ${RADOM_COLORS.GRAY_DARKEST};
  font-size: 10px;
  user-select: none;
`

export const TextAreaWithLabel = (
  { label, value, onChange, placeholder, onKeyDown, required, disabled }: React.HTMLProps<HTMLTextAreaElement>
): ReactElement => {
  return <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
    <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'nowrap', gap: 5 }}>
      <InputLabel>
        <span>{ label }</span>
      </InputLabel>
      {!required && <OptionalLabel>Optional</OptionalLabel>}
    </div>
    <TextArea
      style={{ maxHeight: 100 }}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      onKeyDown={onKeyDown}
      required={required}
      disabled={disabled}
    />
  </div>
}

interface TextWithLabelProps extends React.HTMLProps<HTMLInputElement> {
  tooltip?: string
  inputStyle?: React.CSSProperties
}

const TextInputWithLabel_ = (
  {
    label,
    tooltip,
    value,
    onChange,
    placeholder,
    pattern,
    onKeyDown,
    required,
    disabled,
    ref,
    as,
    style,
    inputStyle,
    ...props
  }: TextWithLabelProps
): ReactElement => {
  return <div style={{ display: 'flex', flexDirection: 'column', gap: 10, ...style }}>
    <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'nowrap', gap: 5 }}>
      <InputLabel><span>{label}</span></InputLabel>
      {!required && <OptionalLabel>Optional</OptionalLabel>}
      {tooltip && <Tooltip message={tooltip} />}
    </div>
    <TextInput
      type="text"
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      pattern={pattern}
      onKeyDown={onKeyDown}
      disabled={disabled}
      required={required}
      style={inputStyle}
      {...props}
    />
  </div>
}

export const TextInputWithLabel = React.memo(TextInputWithLabel_)
export const TextInputWithLabelWithSuggestions = <T extends string>({
  label, type = 'text', value, onChange, placeholder, pattern,
  onKeyDown, required, disabled, suggestionList, setSelectedItems, inputStyle
}: ITextInputWithSuggetsionsProps): ReactElement => {
  const wrapperRef = useRef<HTMLDivElement>(null)
  const [showSuggestions, setShowSuggestions] = useState(false)

  const onItemSelect = (e: React.MouseEvent | React.KeyboardEvent, item: T): void => {
    e.preventDefault()
    e.stopPropagation()
    setSelectedItems([item])
    setShowSuggestions(false)
  }

  useEffect(() => {
    setShowSuggestions((suggestionList?.length ?? 0) > 0)
  }, [suggestionList])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent): void => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setShowSuggestions(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  return (
    <div
      ref={wrapperRef}
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
        position: 'relative',
        width: '100%'
      }}
    >
      <InputLabel>{label}</InputLabel>

      <TextInput
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        pattern={pattern}
        onKeyDown={onKeyDown}
        required={required}
        disabled={disabled}
        style={inputStyle}
      />

      {showSuggestions && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            width: '100%',
            marginTop: 4,
            background: 'white',
            borderRadius: 5,
            boxShadow: `0 0 5px ${RADOM_COLORS.GRAY_DARK}`,
            zIndex: 999,
            fontSize: 14
          }}
        >
          <DropdownContentBody>
            {suggestionList?.map(item => (
              <DropdownListItem
                key={item}
                onClick={(e) => onItemSelect(e, item)}
              >
                {item}
              </DropdownListItem>
            ))}
          </DropdownContentBody>
        </div>
      )}
    </div>
  )
}

export const InputLabel = styled.span`
  font-weight: 400;
  display: flex;
  flex-direction: row;
  gap: 8px;
  align-items: center;
  opacity: 0.5;
  font-size: 14px;
`

export const TextInput = styled.input<{ pattern?: string, disabled?: boolean }>`
  ${BaseInputStyles}

  ${({ pattern }) => pattern && `
    :invalid {
      border: 1px solid ${RADOM_COLORS.ERROR};
    }`
}
`

export const InputWithCheckbox = styled.div`
  padding: 6px;
  display: flex;
  gap: 2px;
  border: 1px solid ${RADOM_COLORS.GRAY_DARK};
  box-shadow: 0 1px 1px ${RADOM_COLORS.GRAY_LIGHTEST};
  border-radius: 4px;

  :focus-within {
    outline: 1px solid ${RADOM_COLORS.GRAY_MED};
  }
`

export const UnstyledInput = styled.input`
  border: 0;
  width: 100%;

  :focus {
    outline: none;
    border: none;
  }
`

export const Cookie = styled.div`
  max-width: min-content;
  background-color: ${RADOM_COLORS.GRAY_MED};
  border-radius: 5px;
  padding: 3px 5px;
  color: ${RADOM_COLORS.GRAY_DARKEST};
  font-size: 12px;
  user-select: none;
`
